'use client';

/**
 * PublicSmoothScroll.tsx — Lenis-aktig «silkemyk» scrolling på fronten (sider uten innlogging)
 *
 * Mountes av ClientUiShell kun på ruter uten UI Shell, dvs. de som ikke krever innlogging (forsiden, produkt- og
 * domenesidene, forside 2–5, /login). Innloggede sider (dashbord m.fl.) får ikke dette.
 *
 * Bruker Lenis (smoothWheel): musehjul og styreflate glir med treghet (lerp) mot målposisjonen, mens scrollposisjonen
 * fortsatt er den vanlige window.scrollY. Derfor virker scroll-lyttere (header som blir solid, WebGL-scenene med parallakse)
 * uendret. Berøring (mobil) scroller native; tastatur og skjermlesere er urørt.
 *
 * - prefers-reduced-motion: Lenis startes ikke (vanlig native scrolling).
 * - Ankerlenker (#produkter, /#moduler på samme side) ruller mykt og respekterer scroll-margin-top (Lenis leser den selv), som native ankre gjør.
 * - Nestede scrollbare flater (mobilmenyens skuff m.fl.) scroller for seg selv (allowNestedScroll + role="dialog").
 * - Ved ruteskifte synkroniseres Lenis mot Next.js sin scroll (til toppen eller til #hash), så ingen gammel treghet ruller videre.
 * Lenis lastes dynamisk, så pakken ikke ligger i bunten til innloggede sider.
 */

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import type Lenis from 'lenis';
import { registerLenis } from '@/lib/smooth-scroll';

export default function PublicSmoothScroll() {
  const lenisRef = useRef<Lenis | null>(null);
  const pathname = usePathname();

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let cancelled = false;
    let removeClick: (() => void) | undefined;

    import('lenis').then(({ default: LenisClass }) => {
      if (cancelled) return;

      const lenis = new LenisClass({
        autoRaf: true,
        lerp: 0.085,
        smoothWheel: true,
        allowNestedScroll: true,
        stopInertiaOnNavigate: true,
        prevent: (node) => node.closest('[role="dialog"], [data-lenis-prevent]') !== null,
      });
      lenisRef.current = lenis;
      registerLenis(lenis);

      // Ankerlenker til et element på samme side: myk scrolling
      const onClick = (event: MouseEvent) => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const anchor = (event.target as Element | null)?.closest?.('a[href]');
        if (!(anchor instanceof HTMLAnchorElement) || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
        const url = new URL(anchor.href, window.location.href);
        if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || !url.hash || url.hash === '#') return;
        const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
        if (!target) return;

        event.preventDefault();
        event.stopPropagation();
        lenis.scrollTo(target); // Lenis tar hensyn til scroll-margin-top selv (headerhøyden), som native ankre
        if (window.location.hash !== url.hash) window.history.pushState(null, '', url.hash);
      };
      document.addEventListener('click', onClick, true);
      removeClick = () => document.removeEventListener('click', onClick, true);
    });

    return () => {
      cancelled = true;
      removeClick?.();
      registerLenis(null);
      lenisRef.current?.destroy();
      lenisRef.current = null;
    };
  }, []);

  // Ruteskifte: la Next.js scrolle (til toppen eller til #hash), og synkroniser deretter Lenis mot den faktiske posisjonen
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      const lenis = lenisRef.current;
      if (!lenis) return;
      lenis.resize();
      lenis.scrollTo(window.scrollY, { immediate: true, force: true });
    });
    return () => cancelAnimationFrame(id);
  }, [pathname]);

  return null;
}
